import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Controller } from 'react-hook-form';

import { Button } from 'primereact/button';
import { Chips } from 'primereact/chips';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { AutoComplete } from 'primereact/autocomplete';
import { Calendar } from 'primereact/calendar';
import { Tooltip } from 'primereact/tooltip';
import { confirmDialog } from 'primereact/confirmdialog';
import { Link } from 'react-router-dom';
import { useToggle } from 'hooks/useToggle';
import { useDebounce } from 'hooks/useDebounce';

import { useToast } from 'context/ToastContext';
import AppConstants from 'constants/index';
import { toastConstant } from 'constants/toastmessage';
import { createVenue } from 'services/Settings';
import { createContact } from 'services/contact';
import { useContactList } from 'hooks/useContactList';
import { updateCaseContactV2 } from 'services/CaseServices';
import { useGlobalTagList } from 'hooks/useGlobalTags';
import { useVenueList } from 'hooks/useVenueList';
import { useOfficeLocation } from 'hooks/useOfficeLocation';
import { useCaseMatter } from 'modules/cases/hooks/useCaseMatter';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { usePracticeAreaList } from 'hooks/usePracticeAreaList';
import moment from 'moment';
import { formatDateWithoutTimezone } from 'utils/utils';
import { parseDOB } from 'modules/lead/helpers/helpers';

const Summary = ({
  control,
  userDetails,
  caseDetails,
  watch,
  setValue,
  fetchCaseStage,
  caseStageList,
  loadCourtContacts,
  courtList,
  isLeadSpecific,
  handleSubmit,
  getCaseDetails,
}) => {
  //----------- custom hook ----------
  const { addToast } = useToast();
  const { practiceAreaList } = usePracticeAreaList(true, 10, 'all');
  const { searchContactsWithFullObjects, searchCourtLocationsWithFullObjects, loadInitialContacts } = useContactList();

  const { modifiedOfficeLocationList } = useOfficeLocation();

  // -----------------useState ------------------------
  const [isAddCourtLoading, setIsAddCourtLoading] = useState(false);
  const [toggleCourt, setToggleCourt] = useState(false);
  const [currentPracticeArea, setCurrentPracticeArea] = useState(caseDetails?.case_description?.case_practice_area);
  const { caseMatterList } = useCaseMatter(currentPracticeArea);
  const { formattedGlobalTag } = useGlobalTagList(currentPracticeArea);
  const { venueOptions, loadVenue } = useVenueList('', currentPracticeArea);

  const [toggleVenue, setToggleVenue] = useState(false);
  const [isAddVenueLoading, setIsAddVenueLoading] = useState(false);

  const [showGlobalTags, setShowGlobalTags] = useState(false);

  const [age, setAge] = useState();
  const clientDetails = caseDetails?.client_details?.[0];

  const [isFullNamedropdown, setToggleFullName] = useToggle(false);

  // Client AutoComplete states
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [clientInputValue, setClientInputValue] = useState('');

  // Court AutoComplete states
  const [selectedCourtId, setSelectedCourtId] = useState(null);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [courtSuggestions, setCourtSuggestions] = useState([]);
  const [courtInputValue, setCourtInputValue] = useState('');

  // Search query states for debouncing
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [courtSearchQuery, setCourtSearchQuery] = useState('');

  // Debounced search queries
  const debouncedClientQuery = useDebounce(clientSearchQuery, 300);
  const debouncedCourtQuery = useDebounce(courtSearchQuery, 300);

  // ---------------custom function----------------------------

  // Get the display value for Client AutoComplete
  const getClientAutoCompleteValue = () => {
    if (clientInputValue) {
      return clientInputValue;
    }
    if (selectedContactId && selectedContact && !clientInputValue) {
      return `${selectedContact?.first_name || ''} ${selectedContact?.middle_name || ''} ${selectedContact?.last_name || ''}`
        .replace(/\s+/g, ' ')
        .trim();
    }
    return '';
  };

  const searchClient = useCallback(async (event) => {
    const query = event.query;

    setClientSearchQuery(query);
  }, []);

  // Search function for Court AutoComplete
  const searchCourt = useCallback(async (event) => {
    const query = event.query;

    setCourtSearchQuery(query);
  }, []);

  const onSelectClient = (e) => {
    if (e.value && e.value.value) {
      setSelectedContactId(e.value.value);
      setSelectedContact(e.value.client);
      setClientInputValue(e.value.label);
    }
  };

  const onSelectCourt = (e) => {
    if (e.value && e.value.value) {
      setSelectedCourtId(e.value.value);
      setSelectedCourt({ title: e.value.label, contact_id: e.value.value });
      setCourtInputValue(e.value.label);
      setValue('case_description.court', e.value.value);
    }
  };

  const getCourtAutoCompleteValue = () => {
    if (courtInputValue) {
      return courtInputValue;
    }
    if (selectedCourtId && selectedCourt) {
      return selectedCourt?.title || '';
    }
    return '';
  };

  const handleAddCaseCourt = () => {
    if (watch('case_description.court') && toggleCourt) {
      setIsAddCourtLoading(true);
      let data = {};
      data = {
        phone: '',
        title: watch('case_description.court'),
        contact_type: 'location',
        is_court_location: true,
        email: '',
        country_code: '+1',
      };
      createContact(data)
        .then((response) => {
          setToggleCourt(false);
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.CASE_COURT_CREATED);
          setIsAddCourtLoading(false);
          const newCourt = response?.data;
          if (newCourt) {
            const courtSuggestion = {
              label: newCourt.title || data.title,
              value: newCourt.contact_id,
            };
            setCourtSuggestions((prev) => {
              const exists = prev.some((suggestion) => suggestion.value === newCourt.contact_id);
              if (!exists) {
                return [courtSuggestion, ...prev];
              }
              return prev;
            });
            setSelectedCourtId(newCourt.contact_id);
            setSelectedCourt({ title: newCourt.title || data.title, contact_id: newCourt.contact_id });
            setCourtInputValue(newCourt.title || data.title);
          }

          loadCourtContacts();
          setValue('case_description.court', response?.data?.contact_id);
        })
        .catch((error) => {
          if (error.response && error.response.data) {
            addToast(false, toastConstant.toasterType.WARN, toastConstant.api.FAILED, error.response.data);
          }
          setIsAddCourtLoading(false);
        });
    } else {
      addToast(false, toastConstant.toasterType.INFO, toastConstant.api.FAILED, toastConstant.message.CASE_COURT_NAME_VALIDATION);
    }
  };

  const handleAddVenue = () => {
    if (watch('case_description.venue') && toggleVenue) {
      setIsAddVenueLoading(true);
      let data = {};
      data = {
        venue_name: watch('case_description.venue'),
      };
      createVenue(data)
        .then((response) => {
          setToggleVenue(false);
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.VENUE_CREATED);
          setIsAddVenueLoading(false);
          loadVenue();
          setValue('case_description.venue', watch('case_description.venue'));
        })
        .catch((error) => {
          if (error.response && error.response.data) {
            addToast(false, toastConstant.toasterType.WARN, toastConstant.api.FAILED, error.response.data);
          }
          setIsAddVenueLoading(false);
        });
    } else {
      addToast(false, toastConstant.toasterType.INFO, toastConstant.api.FAILED, toastConstant.message.VENUE_NAME_VALIDATION);
    }
  };

  const chipTemplate = (e) => {
    if (e) {
      try {
        let formattedTagData = e?.replaceAll('#', '');
        return <div>{'#' + formattedTagData}</div>;
      } catch (error) {
        return <div>{e}</div>;
      }
    }
  };

  const assignToItemTemplate = (option) => {
    if (option?.status === 'ARCHIVED') {
      return `${option?.practice_area_name} (Archived)`;
    }
    return option?.practice_area_name;
  };

  const practiceAreaOptions = useMemo(
    () =>
      practiceAreaList.filter(
        (option) =>
          caseDetails?.case_description?.case_practice_area?.trim() === option?.practice_area_name.trim() || option?.status === 'ACTIVE'
      ),
    [practiceAreaList, caseDetails?.case_description?.case_practice_area]
  );

  useEffect(() => {
    if (clientDetails?.dob) {
      ageCalculation(clientDetails?.dob);
    } else {
      setAge('');
    }
  }, [caseDetails, clientDetails?.dob]);

  useEffect(() => {
    if (caseDetails?.case_description?.case_practice_area) {
      setCurrentPracticeArea(caseDetails?.case_description?.case_practice_area);
    }
  }, [caseDetails?.case_description?.case_practice_area]);

  const ageCalculation = (dob) => {
    if (!dob) return '';
    const DOB = parseDOB(dob);
    if (!DOB) return '';
    const today = new Date();
    const age = today.getFullYear() - DOB.getFullYear();
    setAge(age);
  };

  const handleClientInformationSubmit = async (data, isRetain = false) => {
    const contact = selectedContact;

    if (!isLeadSpecific) {
      await updateCaseContactV2(caseDetails?.case_id, { ...contact, role: undefined }, clientDetails?.sk);
      await getCaseDetails();
      setToggleFullName(false);
    }
  };

  useEffect(() => {
    const currentClient = caseDetails?.client_details?.[0];

    if (currentClient) {
      const currentClientOption = {
        label: `${currentClient?.first_name || ''} ${currentClient?.middle_name || ''} ${currentClient?.last_name || ''}`
          .replace(/\s+/g, ' ')
          .trim(),
        value: currentClient?.contact_id,
        client: currentClient,
      };
      setClientSuggestions((prev) => {
        const exists = prev.some((suggestion) => suggestion.value === currentClient?.contact_id);
        if (!exists) {
          return [currentClientOption, ...prev];
        }
        return prev;
      });
    }
  }, [caseDetails?.client_details]);

  useEffect(() => {
    const performClientSearch = async () => {
      if (debouncedClientQuery && debouncedClientQuery.length > 0) {
        const results = await searchContactsWithFullObjects(debouncedClientQuery);

        let suggestions = [...results];

        const currentClient = caseDetails?.client_details?.[0];
        if (currentClient) {
          const currentClientName = `${currentClient?.first_name || ''} ${currentClient?.middle_name || ''} ${
            currentClient?.last_name || ''
          }`
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase();
          const searchQuery = debouncedClientQuery.toLowerCase();

          if (currentClientName.includes(searchQuery)) {
            const currentClientSuggestion = {
              label: `${currentClient?.first_name || ''} ${currentClient?.middle_name || ''} ${currentClient?.last_name || ''}`
                .replace(/\s+/g, ' ')
                .trim(),
              value: currentClient?.contact_id,
              client: currentClient, // Full contact object
            };

            const isAlreadyInResults = results.some((result) => result.value === currentClient?.contact_id);
            if (!isAlreadyInResults) {
              suggestions.unshift(currentClientSuggestion);
            }
          }
        }

        setClientSuggestions(suggestions);
      } else {
        setClientSuggestions([]);
      }
    };

    performClientSearch();
  }, [debouncedClientQuery, searchContactsWithFullObjects, caseDetails?.client_details]);

  // Set initial client values
  useEffect(() => {
    if (caseDetails?.client_details?.[0]) {
      const client = caseDetails.client_details[0];
      setSelectedContactId(client.contact_id);
      setSelectedContact(client);
      setClientInputValue(
        `${client?.first_name || ''} ${client?.middle_name || ''} ${client?.last_name || ''}`.replace(/\s+/g, ' ').trim()
      );
    }
  }, [caseDetails]);

  // Effect to add current court to suggestions (no initial API call needed)
  useEffect(() => {
    // Only add current court from caseDetails to suggestions if we have the data
    const currentCourtId = caseDetails?.case_description?.court;
    const currentCourtName = caseDetails?.case_description?._court_;

    if (currentCourtId && currentCourtName) {
      const currentCourtOption = {
        label: currentCourtName,
        value: currentCourtId,
      };

      // Add current court to suggestions if not already present
      setCourtSuggestions((prev) => {
        const exists = prev.some((suggestion) => suggestion.value === currentCourtId);
        if (!exists) {
          return [currentCourtOption, ...prev];
        }
        return prev;
      });
    }
  }, [caseDetails?.case_description?.court, caseDetails?.case_description?._court_]);

  // Effect to handle debounced court search (like Defendant component)
  useEffect(() => {
    const performCourtSearch = async () => {
      if (debouncedCourtQuery && debouncedCourtQuery.length > 0) {
        const results = await searchCourtLocationsWithFullObjects(debouncedCourtQuery);

        // Check if current court matches the search query and add it to suggestions
        let suggestions = [...results];

        const currentCourt = caseDetails?.court_details?.[0];
        if (currentCourt) {
          const currentCourtName = (currentCourt?.title || '').toLowerCase();
          const searchQuery = debouncedCourtQuery.toLowerCase();

          // If current court name contains the search query, add it to suggestions
          if (currentCourtName.includes(searchQuery)) {
            const currentCourtSuggestion = {
              label: currentCourt?.title || '',
              value: currentCourt?.contact_id,
            };

            // Check if current court is not already in results
            const isAlreadyInResults = results.some((result) => result.value === currentCourt?.contact_id);
            if (!isAlreadyInResults) {
              suggestions.unshift(currentCourtSuggestion); // Add to beginning
            }
          }
        }

        setCourtSuggestions(suggestions);
      } else {
        setCourtSuggestions([]);
      }
    };

    performCourtSearch();
  }, [debouncedCourtQuery, searchCourtLocationsWithFullObjects, caseDetails?.court_details]);

  // Set initial court values
  useEffect(() => {
    // First check if we have court data from case_description (court ID and _court_ name)
    const courtId = caseDetails?.case_description?.court;
    const courtName = caseDetails?.case_description?._court_;

    if (courtId && courtName) {
      // Use the court data from case_description
      setSelectedCourtId(courtId);
      setSelectedCourt({ title: courtName, contact_id: courtId });
      setCourtInputValue(courtName);
    } else if (caseDetails?.court_details?.[0]) {
      // Fallback to court_details if available
      const court = caseDetails.court_details[0];
      setSelectedCourtId(court.contact_id);
      setSelectedCourt(court);
      setCourtInputValue(court?.title || '');
    }
  }, [caseDetails]);

  // ------------------------------------------------ here start jsx code --------------------------------

  return (
    <>
      <div className="caption-bold">{isLeadSpecific ? 'Potential Case Details' : 'Summary'}</div>
      {/* summary---------------------------------------------------------------  */}
      <Card className="card-bg F-size14">
        <div className="row align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12 text-muted p-medium">Case Name</div>
          <div className="col-md-8 col-12 d-flex align-items-center edit-field">
            <Controller
              name="case_description.case_name"
              control={control}
              render={({ field }) => (
                <TextSnippetWrapper
                  type="text"
                  className="input-shadow w-100"
                  placeholder="Enter Case Name"
                  {...field}
                  disabled={userDetails?.getPermission('cases') !== AppConstants.permissions.WRITE}
                />
              )}
            />
          </div>
        </div>
        {caseDetails?.case_description?._case_practice_area_ !== 'Personal Injury' && !isLeadSpecific && (
          <>
            <div className="row align-items-center py-2 flex-wrap">
              <div className="col-md-4 col-12 text-muted p-medium">Client's Full Name</div>
              <div className="col-md-8 col-12 d-flex align-items-center edit-field">
                {isFullNamedropdown ? (
                  <>
                    <Controller
                      name="client_details.full_name"
                      control={control}
                      render={({ field }) => (
                        <AutoComplete
                          placeholder="Search and Select Client"
                          style={{ width: '100%' }}
                          inputStyle={{ width: '100%' }}
                          value={getClientAutoCompleteValue()}
                          suggestions={clientSuggestions}
                          onSelect={(e) => onSelectClient(e)}
                          completeMethod={searchClient}
                          field="label"
                          onFocus={async () => {
                            // Load initial suggestions when focused (like Defendant component)
                            if (clientSuggestions.length === 0) {
                              try {
                                const suggestions = await loadInitialContacts();
                                setClientSuggestions(suggestions);
                              } catch (error) {
                                console.log(error);
                              }
                            }
                          }}
                          onChange={(e) => {
                            field.onChange(e.value);
                            // Update input value for typing
                            setClientInputValue(e.value);
                            // Update search query for debouncing
                            setClientSearchQuery(e.value || '');
                            // Clear selection if user types manually and it's different from selected contact
                            const currentContactName = selectedContact
                              ? `${selectedContact?.first_name || ''} ${selectedContact?.middle_name || ''} ${
                                  selectedContact?.last_name || ''
                                }`
                                  .replace(/\s+/g, ' ')
                                  .trim()
                              : '';
                            if (e.value && typeof e.value === 'string' && e.value !== currentContactName) {
                              setSelectedContactId(null);
                              setSelectedContact(null);
                            } else if (!e.value) {
                              // Clear everything when field is empty
                              setSelectedContactId(null);
                              setSelectedContact(null);
                            }
                          }}
                          className="flex-grow-1 input-shadow"
                        />
                      )}
                    />

                    <Button
                      icon="pi pi-times"
                      className="p-button-secondary outline mx-2"
                      type="button"
                      onClick={() => {
                        setToggleFullName();
                        setSelectedContactId(clientDetails?.contact_id);
                      }}
                    />
                    <Button
                      icon="pi pi-check"
                      className="p-button-secondary outline"
                      type="button"
                      onClick={() => handleSubmit((data) => handleClientInformationSubmit(data, false))()}
                    />
                  </>
                ) : (
                  <>
                    <Link to={`/contacts/${caseDetails?.client_details?.[0]?.contact_id || ''}`} className="text-decoration-none">
                      <div className="title-link-wrap cursor-pointer">
                        <h5 className="title-text mb-0 redirection-link-no-text-transform">
                          {`${clientDetails?.first_name} ${clientDetails?.middle_name || ''} ${clientDetails?.last_name}`
                            .replace(/\s+/g, ' ')
                            .trim()}
                        </h5>
                      </div>
                    </Link>
                    <div className="col-1 col-md-1 px-2 d-flex align-items-center ms-1 w-auto">
                      <span className="pointer">
                        <i
                          className="icon-edit"
                          onClick={() => {
                            setToggleFullName(true);
                          }}
                        ></i>
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="row align-items-center py-2 flex-wrap">
              <div className="col-md-4 col-12 text-muted p-medium">Client's DOB</div>
              <div className="col-md-8 col-12 d-flex align-items-center edit-field">
                <span>{clientDetails?.dob ? formatDateWithoutTimezone(clientDetails?.dob) : '-'}</span>
              </div>
            </div>
            <div className="row align-items-center py-2 flex-wrap">
              <div className="col-md-4 col-12 text-muted p-medium">Client's Age</div>
              <div className="col-md-8 col-12 d-flex align-items-center edit-field">
                <span>{age}</span>
              </div>
            </div>
          </>
        )}

        <div className="row align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12 text-muted p-medium">Practice Area</div>
          <div className="col-md-8 col-12 d-flex align-items-center edit-field">
            <Controller
              name="case_description.case_practice_area"
              control={control}
              render={({ field }) => (
                <Dropdown
                  value={field.value}
                  options={practiceAreaOptions}
                  onChange={(e) => {
                    const newValue = e.value;
                    const oldValue = currentPracticeArea;

                    if (newValue !== oldValue) {
                      confirmDialog({
                        message:
                          'Changing the practice area will impact associated billing codes and other dependent configurations. Do you want to continue.',
                        header: 'Change Practice Area',
                        icon: 'pi pi-exclamation-triangle',
                        accept: () => {
                          field.onChange(newValue);
                          setCurrentPracticeArea(newValue);
                          fetchCaseStage(newValue);
                          let dat = caseStageList.find((val) => val.stage_name === 'Open');
                          if (dat?.stage_name) {
                            setValue('case_description.case_stage', dat?.stage_name);
                          }
                        },
                        reject: () => {
                          field.onChange(oldValue);
                        },
                        closable: false,
                      });
                    }
                  }}
                  disabled={userDetails?.getPermission('cases') !== AppConstants.permissions.WRITE}
                  optionLabel="practice_area_name"
                  optionValue="practice_area_id"
                  placeholder="Select Practice Area"
                  className="input-shadow w-100"
                  itemTemplate={assignToItemTemplate}
                  filter
                />
              )}
            />
          </div>
        </div>
        <div className="row align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12 text-muted p-medium">Case Matter</div>
          <div className="col-md-8 col-12 d-flex align-items-center edit-field">
            <Controller
              name="case_description.case_matter"
              control={control}
              render={({ field }) => (
                <Dropdown
                  name="case_matter"
                  value={field.value}
                  onChange={(event) => field.onChange(event.value)}
                  options={caseMatterList}
                  optionLabel="matter_name"
                  optionValue="case_matter_id"
                  placeholder="Select Case Matter"
                  disabled={userDetails?.getPermission('cases') !== AppConstants.permissions.WRITE}
                  className="flex-grow-1 w-75 border-0 input-shadow"
                />
              )}
            />
          </div>
        </div>

        <div className="row align-items-start py-2 flex-wrap">
          <div className="col-md-4 col-12 text-muted p-medium">Description</div>
          <div className="col-md-8 col-12 edit-field d-flex align-items-center">
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextSnippetWrapper
                  type="textarea"
                  rows={16}
                  cols={30}
                  value={field.value}
                  placeholder="Enter Description"
                  onChange={(e) => field.onChange(e.target.value)}
                  className="input-shadow w-100"
                  disabled={userDetails?.getPermission('cases') !== AppConstants.permissions.WRITE}
                />
              )}
            />
          </div>
        </div>
        <div className="row align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12 p-medium">Office Location</div>
          <div className="col-md-8 col-12">
            <Controller
              name="case_description.office_location"
              control={control}
              render={({ field }) => (
                <Dropdown
                  placeholder="Select Office Location"
                  value={field.value}
                  options={modifiedOfficeLocationList}
                  onChange={(e) => {
                    field.onChange(e.value);
                  }}
                  optionValue="office_location_id"
                  optionLabel="office_nameLabel"
                  className="input-shadow border-0 w-100"
                  filter
                />
              )}
            />
          </div>
        </div>
        <div className="row align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12 p-medium">
            <label className="p-medium">Court</label>
          </div>
          <div className="d-flex col-md-8 col-12">
            {toggleCourt ? (
              <div className="p-inputgroup align-items-center">
                <Controller
                  name="case_description.court"
                  control={control}
                  render={({ field }) => (
                    <TextSnippetWrapper
                      type="text"
                      value={field.value}
                      placeholder="Enter New Court"
                      onChange={(e) => field.onChange(e.target.value.trimStart())}
                      className="input-shadow w-100"
                    />
                  )}
                />
                <Button
                  icon="pi pi-check"
                  className="p-button-secondary mx-2"
                  type="button"
                  onClick={handleAddCaseCourt}
                  loading={isAddCourtLoading}
                />
                <Button
                  icon="pi pi-times"
                  className="p-button-secondary outline"
                  type="button"
                  onClick={() => {
                    setToggleCourt(false);
                    setValue('case_description.court', watch('case_description.court'));
                  }}
                  disabled={isAddCourtLoading}
                />
              </div>
            ) : (
              <>
                <Controller
                  name="case_description.court"
                  control={control}
                  render={({ field }) => (
                    <AutoComplete
                      placeholder="Search and Select Court"
                      style={{ width: '100%' }}
                      inputStyle={{ width: '100%' }}
                      value={getCourtAutoCompleteValue()}
                      suggestions={courtSuggestions}
                      onSelect={(e) => onSelectCourt(e)}
                      completeMethod={searchCourt}
                      field="label"
                      onFocus={async () => {
                        // Load initial suggestions when focused (like Defendant component)
                        if (courtSuggestions.length === 0) {
                          try {
                            const suggestions = await searchCourtLocationsWithFullObjects('');
                            setCourtSuggestions(suggestions);
                          } catch (error) {
                            console.log(error);
                          }
                        }
                      }}
                      onChange={(e) => {
                        // Update search query for debouncing
                        setCourtSearchQuery(e.value || '');
                        // Only update the form field when user is typing manually
                        if (e.value && typeof e.value === 'string') {
                          field.onChange(e.value);
                          setCourtInputValue(e.value);
                          // Clear selection if user types manually
                          setSelectedCourtId(null);
                          setSelectedCourt(null);
                        } else if (!e.value) {
                          // Clear everything when field is empty
                          field.onChange('');
                          setCourtInputValue('');
                          setSelectedCourtId(null);
                          setSelectedCourt(null);
                        }
                      }}
                      className="input-shadow w-100"
                    />
                  )}
                />
              </>
            )}
            {userDetails.permissions.settings.add_court_location && (
              <div className="col-1 col-md-1 px-1 d-flex align-items-center ms-1 w-auto">
                <span className="pointer">
                  <i
                    className="fas fa-plus-circle add-court"
                    onClick={() => {
                      setToggleCourt(true);
                      setValue('case_description.court', '');
                    }}
                  ></i>
                  <Tooltip content="Add Court" position="top" target=".add-court" showDelay={500} />
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="row align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12 p-medium">
            <label className="p-medium">Venue</label>
          </div>
          <div className="d-flex col-md-8 col-12">
            {toggleVenue ? (
              <div className="p-inputgroup align-items-center">
                <Controller
                  name="case_description.venue"
                  control={control}
                  render={({ field }) => (
                    <TextSnippetWrapper
                      type="text"
                      value={field.value}
                      placeholder="Enter New Venue"
                      onChange={(e) => field.onChange(e.target.value.trimStart())}
                      className="input-shadow w-100"
                    />
                  )}
                />
                <Button
                  icon="pi pi-check"
                  className="p-button-secondary mx-2"
                  type="button"
                  onClick={handleAddVenue}
                  loading={isAddVenueLoading}
                />
                <Button
                  icon="pi pi-times"
                  className="p-button-secondary outline"
                  type="button"
                  onClick={() => {
                    setToggleVenue(false);
                    setValue('case_description.venue', watch('case_description.venue'));
                  }}
                  disabled={isAddVenueLoading}
                />
              </div>
            ) : (
              <>
                <Controller
                  name="case_description.venue"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      name="venue"
                      value={field.value}
                      options={venueOptions}
                      optionLabel="venue_name"
                      optionValue="venue_id"
                      onChange={(e) => field.onChange(e.value)}
                      placeholder="Select Venue"
                      className="input-shadow w-100"
                      filter
                    />
                  )}
                />
              </>
            )}
            <div className="col-1 col-md-1 px-1 d-flex align-items-center ms-1 w-auto">
              <span className="pointer">
                <i
                  className="fas fa-plus-circle add-venue"
                  onClick={() => {
                    setToggleVenue(true);
                    setValue('case_description.venue', '');
                  }}
                ></i>
                <Tooltip content="Add Venue" position="top" target=".add-venue" showDelay={500} />
              </span>
            </div>
          </div>
        </div>

        <div className="row align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12 text-muted p-medium">Date of Loss/Event</div>
          <div className="col-md-8 col-12 edit-field">
            <Controller
              name="case_description.date_of_loss_event"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Calendar
                  monthNavigator
                  yearNavigator
                  yearRange="1900:2100"
                  showButtonBar
                  dateFormat={AppConstants.date_format}
                  value={value}
                  onChange={(e) => onChange(e.value)}
                  className="rounded flex-grow-1 w-100 input-shadow"
                  placeholder="Select"
                />
              )}
            />
          </div>
        </div>

        <div className="row align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12 text-muted p-medium">Tags</div>
          <div className="col-md-8 col-12 edit-field">
            <Controller
              name="tags"
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <Chips
                  itemTemplate={chipTemplate}
                  placeholder="Select Tags"
                  value={field.value}
                  onChange={(e) => {
                    let arr = [];
                    if (e?.value?.length) {
                      arr = e.value.map((val) => val.replace(/\s+/g, ''));
                    }
                    field.onChange([...new Set(arr || e.target.value)]);
                  }}
                  className="input-shadow w-100 taging lead-tags"
                />
              )}
            />
          </div>
        </div>

        {userDetails.checkSubPermission('tags') && (
          <div className="row align-items-center py-2 flex-wrap">
            <div className="col-12 text-muted p-medium">
              <Button
                type="button"
                className="p-button button-text text-medium addfield ps-0"
                onClick={(e) => {
                  e.preventDefault();
                  setShowGlobalTags(!showGlobalTags);
                }}
              >
                {<i className={showGlobalTags ? 'pi pi-minus F-size12 me-1' : 'icon-add F-size14 me-1 '} />}Add Global Tags
              </Button>
            </div>
            <div className="col-12 edit-field">
              {showGlobalTags && (
                <>
                  {formattedGlobalTag?.length > 0 ? (
                    <div className="global-tag-wrapper input-shadow lead-tags">
                      {formattedGlobalTag?.map((element, index) => {
                        let hashReplacedTag = element.value.replace('#', '');
                        return (
                          <span
                            key={index}
                            className={`tag-highlight ${watch('tags')?.includes(hashReplacedTag) ? 'selected-global-tag' : ''}`}
                            onClick={(e) => {
                              let existingTags = watch('tags');
                              existingTags?.push(hashReplacedTag);
                              setValue('tags', [...new Set(existingTags)]);
                            }}
                          >
                            {element.value}
                          </span>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="row mb-2">
                      <div className="col-12 small d-flex align-items-center">
                        <i className="fas fa-info-circle me-2"></i>
                        <span className="text-hint ms-1">No Global Tags Found.</span>{' '}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </Card>
    </>
  );
};

export default Summary;
